<template>
  <figure class="project-showcase" :class="{ 'is-keyboard-change': keyboardChange, 'is-contextual': contextual }" @click.stop @keydown.stop>
    <div :id="`${uid}-panel`" class="project-showcase-panel" :style="{ aspectRatio: `${current.width} / ${current.height}` }" :role="scene || scenes.length < 2 ? null : 'tabpanel'" :aria-labelledby="scene || scenes.length < 2 ? null : `${uid}-tab-${activeIndex}`">
      <Transition name="showcase-scene" :css="!keyboardChange" @before-leave="deactivateScene">
      <div :key="current.src" class="project-showcase-scene">
      <div v-if="current.kind==='gallery-film'" class="project-showcase-film"><GalleryFilm :dark="false" :active="!viewerOpen" unframed /></div>
      <div v-else-if="current.kind==='slidepoise-film'" class="project-showcase-film"><SlidePoiseFilm :active="!viewerOpen" /></div>
      <div v-else-if="current.kind==='cathay-flex-film'" class="project-showcase-film"><CathayFlexFilm :active="!viewerOpen" /></div>
      <button v-else type="button" class="project-showcase-art image-enlarge" :aria-label="`Enlarge ${current.label}`" aria-haspopup="dialog" @click="openViewer">
        <AnimatedProjectArtwork v-if="current.motion" :key="current.motion" :composition="current.motion" :playback-rate="2" :fallback="current.src" :alt="current.alt" />
        <template v-else><img v-for="(item, index) in scenes" :key="item.src" :src="item.src"
          :srcset="`${item.smallSrc} 800w, ${item.src} 1600w`"
          sizes="(max-width: 720px) 90vw, (max-width: 960px) 85vw, 960px"
          :width="item.width" :height="item.height"
          :alt="index === activeIndex ? item.alt : ''" :aria-hidden="index !== activeIndex"
          :class="{ 'is-active': index === activeIndex }" loading="lazy" decoding="async"></template>
      </button>
      </div>
      </Transition>
    </div>
    <div v-if="(scenes.length > 1 && (!scene || !contextual)) || showControls" class="project-showcase-controls">
      <div v-if="!scene" class="project-showcase-tabs" :style="{ '--scene-count': scenes.length }" role="tablist" :aria-label="`${showcase.title} examples`" @keydown="onTabKey">
        <button v-for="(item, index) in scenes" :id="`${uid}-tab-${index}`" :key="item.src"
          type="button" role="tab" :aria-selected="index === activeIndex" :tabindex="index === activeIndex ? 0 : -1"
          :aria-controls="`${uid}-panel`" @click="select(index, $event.detail === 0)">
          <span class="project-showcase-thumbnail" :class="{'is-film':item.kind, 'is-slidepoise-film':item.kind==='slidepoise-film'}">
            <img :src="item.smallSrc" width="800" height="590" alt="" loading="lazy" decoding="async">
            <span v-if="item.kind" class="project-showcase-thumbnail-play" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10.5"/><path d="m10 7 7 5-7 5Z"/></svg></span>
          </span>
          <span class="project-showcase-tab-label"><small aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</small>{{ item.tabLabel || item.label }}</span>
        </button>
      </div>
      <span v-else-if="!contextual" class="project-showcase-scene-label">{{ current.label }}</span>
      <div v-if="showControls" class="project-showcase-art-controls">
      <button type="button" class="project-showcase-expand" :aria-label="`Enlarge ${current.label}`" aria-haspopup="dialog" @click="openViewer">
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M12 3h5v5M17 3l-5.5 5.5M8 17H3v-5m0 5 5.5-5.5" /></svg>
      </button>
      </div>
    </div>
    <figcaption v-if="showCaption && !contextual"><span v-for="(item,index) in scenes" :key="item.src" :class="{ 'is-active': index === activeIndex }" :aria-hidden="index !== activeIndex">{{ item.caption }}</span></figcaption>
    <Teleport to="body">
      <dialog ref="viewer" class="project-artwork-viewer" :aria-label="`${showcase.title} artwork`" @close="onClose" @click="closeOnBackdrop" @keydown="onViewerKey">
        <header class="project-artwork-viewer-head">
          <span>{{ showcase.title }}<small>{{ current.label }}</small></span>
          <div>
            <button type="button" aria-label="Close artwork" autofocus @click="closeViewer"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg></button>
          </div>
        </header>
        <div class="project-artwork-viewer-body" :class="{'is-film':current.kind}">
          <GalleryFilm v-if="viewerOpen && current.kind==='gallery-film'" class="project-showcase-viewer-film" :dark="false" unframed />
          <SlidePoiseFilm v-else-if="viewerOpen && current.kind==='slidepoise-film'" class="project-showcase-viewer-film" />
          <CathayFlexFilm v-else-if="viewerOpen && current.kind==='cathay-flex-film'" class="project-showcase-viewer-film" />
          <img v-else-if="viewerOpen" :src="current.src" :width="current.width" :height="current.height" :alt="current.alt">
        </div>
        <footer class="project-artwork-viewer-foot" :class="{ 'has-no-caption': !showCaption }">
          <p v-if="showCaption">{{ current.caption }}</p>
          <div v-if="scenes.length > 1">
            <button type="button" aria-label="Previous artwork" @click="step(-1)"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M16 10H4m5-5-5 5 5 5" /></svg></button>
            <span aria-live="polite">{{ activeIndex + 1 }} / {{ scenes.length }}</span>
            <button type="button" aria-label="Next artwork" @click="step(1)"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12m-5-5 5 5-5 5" /></svg></button>
          </div>
        </footer>
      </dialog>
    </Teleport>
  </figure>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import AnimatedProjectArtwork from './AnimatedProjectArtwork.vue';
import GalleryFilm from './GalleryFilm.vue';
import SlidePoiseFilm from './SlidePoiseFilm.vue';
import CathayFlexFilm from './CathayFlexFilm.vue';
import { projectShowcases } from '../../data/projectShowcases';

// This player belongs to the Builds preview, outside the indexed artwork scenes.
const galleryFilm = {
  kind: 'gallery-film', label: 'The View Gallery', tabLabel: 'The Gallery',
  src: '/assets/video/view-gallery/poster.webp', smallSrc: '/assets/video/view-gallery/poster.webp',
  width: 1920, height: 1080, alt: 'The View Gallery architectural model',
  caption: 'Walk through the Gallery and see how a question becomes an exhibition.',
};
const slidePoiseFilm = {
  kind: 'slidepoise-film', label: 'SlidePoise introduction', tabLabel: 'Meet SlidePoise',
  src: '/assets/video/slidepoise/poster.webp', smallSrc: '/assets/video/slidepoise/poster.webp',
  width: 1920, height: 1080, alt: 'SlidePoise. Let the content shape the slide.',
  caption: 'See how SlidePoise creates editable presentations with an AI Agent.',
};

export default {
  name: 'ProjectShowcase',
  components: { AnimatedProjectArtwork, GalleryFilm, SlidePoiseFilm, CathayFlexFilm },
  props: {
    projectId: { type: String, required: true },
    // A case study can place individual compositions beside the relevant argument.
    scene: { type: String, default: '' },
    contextual: { type: Boolean, default: false },
    showControls: { type: Boolean, default: true },
    showCaption: { type: Boolean, default: true },
  },
  data() {
    const showcase = projectShowcases[this.projectId];
    const activeIndex = this.scene || ['searchstory', 'slidepoise'].includes(this.projectId) ? 0 : Math.max(0, showcase.scenes.findIndex(item => item.label === showcase.defaultScene));
    return { activeIndex, keyboardChange: false, viewerOpen: false };
  },
  computed: {
    uid() { return `project-showcase-${this.$.uid}`; },
    showcase() { return projectShowcases[this.projectId]; },
    scenes() {
      if (this.scene) return this.showcase.scenes.filter(item => item.label === this.scene);
      if (this.projectId === 'searchstory') return [galleryFilm, ...this.showcase.scenes];
      if (this.projectId === 'slidepoise') return [slidePoiseFilm, ...this.showcase.scenes];
      return this.showcase.scenes;
    },
    current() { return this.scenes[this.activeIndex] || this.scenes[0]; }
  },
  beforeUnmount() { if (this.viewerOpen) this.onClose(); },
  methods: {
    select(index, keyboard = false) { this.keyboardChange = keyboard; this.activeIndex = index; },
    deactivateScene(element) {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
      element.querySelector('video')?.pause();
    },
    step(direction) { this.select((this.activeIndex + direction + this.scenes.length) % this.scenes.length, true); },
    onTabKey(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.select(0, true);
      else if (event.key === 'End') this.select(this.scenes.length - 1, true);
      else this.step(event.key === 'ArrowRight' ? 1 : -1);
      this.$nextTick(() => this.$el.querySelector(`#${this.uid}-tab-${this.activeIndex}`)?.focus());
    },
    openViewer() {
      this._trigger = document.activeElement;

      this.viewerOpen = true;
      this._releaseScroll ||= lockBodyScroll();
      this.$refs.viewer.showModal();
    },
    closeViewer() { this.$refs.viewer.close(); },
    onClose() {
      if (!this.viewerOpen) return;
      this.viewerOpen = false;
      this._releaseScroll?.();this._releaseScroll=null;
      if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
    },
    closeOnBackdrop(event) { if (event.target === this.$refs.viewer) this.closeViewer(); },
    onViewerKey(event) {
      event.stopPropagation();
      if (event.target.closest?.('video')) return;
      if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }
    }
  }
};
</script>

<style>
.project-showcase {
  --showcase-ink: #30302f;
  --showcase-muted: #73736e;
  --showcase-rule: rgb(60 60 50 / 16%);
  min-width: 0;
  margin: 0;
  color: var(--showcase-ink);
  cursor: var(--site-default-cursor);
}
.project-showcase button:where(:not(.gallery-film-play):not(.slidepoise-film-play):not(.cathay-flex-film-play)) {
  appearance: none;
  border: 0;
  border-radius: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
}
.project-showcase button:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.project-showcase-panel { position:relative; overflow:hidden; background:#eeebe4; transition:aspect-ratio 280ms cubic-bezier(.22,.68,0,1); }
.project-showcase-scene { position:absolute; inset:0; display:grid; align-content:center; }
.showcase-scene-enter-active,.showcase-scene-leave-active { transition:opacity 220ms cubic-bezier(.22,.68,0,1); }
.showcase-scene-enter-from,.showcase-scene-leave-to { opacity:0; }
.showcase-scene-leave-active { pointer-events:none; }
.project-showcase .project-showcase-art:focus-visible { outline-offset:-4px; }
.project-showcase-film{display:grid;align-items:center}
.project-showcase-thumbnail.is-film{aspect-ratio:1600/1180;background:#f5f3ed}
.project-showcase-thumbnail.is-film img{height:100%;object-fit:contain}
.project-showcase-thumbnail.is-film{container-type:inline-size}
.project-showcase-thumbnail-play{position:absolute;right:16%;top:54%;color:#30302f}
.project-showcase-thumbnail-play svg{display:block;width:clamp(12px,18cqw,26px);height:auto;fill:none;stroke:currentColor;stroke-width:1}
.project-showcase-thumbnail-play path{fill:currentColor;stroke:none}
.project-showcase-thumbnail.is-slidepoise-film{background:#fffefa}
.is-slidepoise-film .project-showcase-thumbnail-play{left:50%;right:auto;top:67%;transform:translate(-50%,-50%)}
.is-slidepoise-film .project-showcase-thumbnail-play svg{width:clamp(12px,14cqw,20px)}
.project-artwork-viewer-body.is-film{container-type:size;overflow:hidden}
.project-artwork-viewer .project-artwork-viewer-body .project-showcase-viewer-film{width:min(100cqw,calc(100cqh * 16 / 9))}
.project-showcase .project-showcase-art {
  display: grid;
  width: 100%;
  padding: 0;
  aspect-ratio: 1600 / 1180;
  overflow: hidden;
  cursor: var(--site-zoom-in-cursor);
}
.project-showcase-art img {
  grid-area: 1 / 1;
  display: block;
  width: 100%;
  height: auto;
  max-width: none;
  margin: 0;
  border: 0;
  border-radius: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 220ms cubic-bezier(0.23, 1, 0.32, 1);
}
.project-showcase-art img.is-active { opacity: 1; }
.project-showcase-art-controls { display:flex; align-items:center; margin-left:auto; }
.project-showcase-controls { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-top: 18px; }
.project-showcase.is-contextual .project-showcase-controls { justify-content: flex-end; margin-top: 0; }
.project-showcase-tabs { display: grid; grid-template-columns: repeat(var(--scene-count), minmax(0, 1fr)); gap: 16px; flex: 1; min-width: 0; }
.project-showcase-tabs button {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 64px;
  padding: 0;
  color: var(--showcase-muted);
  font-family: 'Quattrocento Sans', sans-serif;
  font-size: 13px;
  line-height: 1.3;
  cursor: pointer;
  transition: color 150ms ease;
}
.project-showcase-thumbnail { position: relative; flex: 0 1 102px; min-width: 0; }
.project-showcase-thumbnail img { display: block; width: 100%; height: auto; margin: 0; opacity: .88; transition: opacity 150ms ease; }
.project-showcase-thumbnail::after { position: absolute; content: ''; height: 2px; bottom: -5px; inset-inline: 0; background: var(--showcase-ink); opacity: 0; transition:opacity 150ms ease; }
.project-showcase-tab-label { display: grid; flex: 1; gap: 5px; min-width: min-content; }
.project-showcase-tab-label small { font-size: 10px; line-height: 1; font-variant-numeric: tabular-nums; opacity: .7; }
.project-showcase-tabs button[aria-selected="true"] { color: var(--showcase-ink); }
.project-showcase-tabs button[aria-selected="true"] img { opacity: 1; }
.project-showcase-tabs button[aria-selected="true"] .project-showcase-thumbnail::after { opacity: 1; }
.project-showcase-scene-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 44px;
  padding: 0;
  color: var(--showcase-muted);
  font-family: 'Quattrocento Sans', sans-serif;
  font-size: 12px;
  line-height: 1.3;
  cursor: pointer;
  transition: color 150ms ease;
}
.project-showcase-tabs button[aria-selected="true"] { color: var(--showcase-ink); }
.project-showcase-tab-dot { flex: 0 0 4px; width: 4px; height: 4px; background: currentColor; border-radius: 50%; opacity: 0; }
.project-showcase-tabs button[aria-selected="true"] .project-showcase-tab-dot { opacity: 1; }
.project-showcase-scene-label { display: flex; align-items: center; min-height: 44px; cursor: var(--site-default-cursor); color: var(--showcase-ink); font: 12px/1.4 'Quattrocento Sans', sans-serif; }
.project-showcase .project-showcase-expand { display: grid; place-items: center; flex: 0 0 44px; width: 44px; height: 44px; padding: 11px; color: var(--showcase-muted); cursor: var(--site-zoom-in-cursor); }
.project-showcase-art-controls svg, .project-artwork-viewer svg { display: block; width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.3; stroke-linecap: round; stroke-linejoin: round; }
.project-showcase figcaption { display:grid; margin: 14px 0 0; min-height: 2.9em; max-width: none; font: 12px/1.55 'Quattrocento Sans', sans-serif; color: var(--showcase-muted); }
.project-showcase figcaption > span { grid-area:1 / 1; opacity:0; visibility:hidden; transition:opacity 180ms ease,visibility 180ms; }
.project-showcase figcaption > span.is-active { opacity:1; visibility:visible; }
.project-showcase.is-keyboard-change img,.project-showcase.is-keyboard-change .project-showcase-panel,.project-showcase.is-keyboard-change figcaption > span { transition:none; }
.project-artwork-viewer {
  --viewer-ink: #33332e;
  box-sizing: border-box;
  position: fixed;
  inset: 0;
  width: calc(100vw - 32px);
  height: calc(100dvh - 32px);
  max-width: 1900px;
  max-height: none;
  padding: 0;
  border: 0;
  background: #f5f3ee;
  color: var(--viewer-ink);
  overflow: hidden;
}
.project-artwork-viewer[open] { display: flex; flex-direction: column; }
.project-artwork-viewer::backdrop { background: rgb(20 20 18 / 75%); }
.project-artwork-viewer button:where(:not(.gallery-film-play):not(.slidepoise-film-play):not(.cathay-flex-film-play)) { appearance: none; border: 0; background: none; color: inherit; font: 13px/1.4 'Quattrocento Sans', sans-serif; cursor: pointer; padding: 12px; min-height: 44px; min-width: 44px; }
.project-artwork-viewer button:focus-visible { outline: 2px solid currentColor; outline-offset: -3px; }
.project-artwork-viewer-head, .project-artwork-viewer-foot { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 8px 20px; font: 13px/1.5 'Quattrocento Sans', sans-serif; }
.project-artwork-viewer-head small { display: inline-block; font: inherit; margin-left: 16px; opacity: .65; }
.project-artwork-viewer-head > div, .project-artwork-viewer-foot > div { display: flex; align-items: center; flex-shrink: 0; gap: 8px; }
.project-artwork-viewer-body { min-height: 0; flex: 1; display: grid; place-items: center; overflow: auto; overscroll-behavior: contain; }
.project-artwork-viewer-body > img { display: block; width: 100%; height: 100%; min-height: 0; max-height: 100%; object-fit: contain; margin: 0; }
.project-artwork-viewer-foot p { margin: 0; min-width: 0; font: inherit; }
.project-artwork-viewer-foot.has-no-caption { justify-content:flex-end; }
.project-artwork-viewer-foot span { font-variant-numeric: tabular-nums; white-space: nowrap; }
body.dark-mode .project-showcase { --showcase-ink: #e1e0da; --showcase-muted: #a19f99; --showcase-rule: rgb(230 230 220 / 16%); }
body.dark-mode .project-artwork-viewer { background: #242421; --viewer-ink: #e1e0da; }
@media (hover: hover) and (pointer: fine) {
  .project-showcase-tabs button:hover img { opacity: 1; }
  .project-showcase-tabs button:hover, .project-showcase-expand:hover { color: var(--showcase-ink); }
  .project-artwork-viewer button:not(.gallery-film-play):not(.slidepoise-film-play):not(.cathay-flex-film-play):hover { background: rgb(125 125 115 / 10%); }
}
@media (max-width: 720px) {
  .project-showcase-controls { flex-wrap: wrap; }
  .project-showcase-tabs { flex-basis: 100%; gap: 12px; }
  .project-showcase-tabs button { flex-direction: column; align-items: stretch; font-size: 12px; gap: 12px; }
  .project-showcase-thumbnail { flex: 0 0 auto; }
  .project-showcase-tab-label { min-width: 0; }
  .project-showcase figcaption { min-height: 4.65em; }
  .project-showcase-expand { align-self: flex-start; }
  .project-artwork-viewer { width: 100vw; height: 100dvh; }
  .project-artwork-viewer-head, .project-artwork-viewer-foot { padding: 8px 12px; }
  .project-artwork-viewer-head small { display: block; margin-left: 0; font-size: 11px; }
  .project-artwork-viewer-foot { align-items: flex-start; }
  .project-artwork-viewer-foot p { font-size: 11px; }
}
@media (prefers-reduced-motion: reduce) {
  .project-showcase *, .project-artwork-viewer * { transition: none !important; }
}
</style>
